import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../services/api'
import { X, ExternalLink, AlertTriangle, AlertCircle, Info } from 'lucide-react'
import { cn } from '../lib/utils'

interface NewsSignal {
  id: string
  title: string
  severity: 'critical' | 'high' | 'info'
  source: string
  link: string
  publishedAt: string
  ruleName: string
  summary: string
}

const DISMISS_KEY = 'cmmc-news-banner-dismissed'
const REFRESH_INTERVAL_MS = 30 * 60 * 1000 // 30 minutes

/**
 * Dismissible regulatory news banner shown below the preview banner in
 * Layout. Pulls the latest critical/high signal from the cmmc-news-agent DB
 * (via GET /api/news/latest) and auto-refreshes on a 30-minute interval.
 */
export function NewsBanner() {
  const [visible, setVisible] = useState(true)
  const [dismissedId, setDismissedId] = useState<string | null>(null)

  // On mount, read the dismissed signal id from localStorage so re-showing
  // the same signal isn't forced on the user.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(DISMISS_KEY)
      if (stored) setDismissedId(stored)
    } catch {
      // localStorage unavailable — ignore.
    }
  }, [])

  const { data, isLoading } = useQuery({
    queryKey: ['latestNews'],
    queryFn: () => apiClient.getLatestNews(),
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: false,
  })

  const signal: NewsSignal | null = data?.data?.signal ?? null

  const handleDismiss = useCallback(() => {
    setVisible(false)
    if (signal) {
      try {
        localStorage.setItem(DISMISS_KEY, signal.id)
      } catch {
        // ignore
      }
    }
  }, [signal])

  // Re-show if a new signal arrives that the user hasn't dismissed yet.
  useEffect(() => {
    if (signal && signal.id !== dismissedId) {
      setVisible(true)
    }
  }, [signal, dismissedId])

  if (isLoading || !visible || !signal) return null

  const critical = signal.severity === 'critical'
  const high = signal.severity === 'high'

  return (
    <div
      role="alert"
      className={cn(
        'border-b px-4 py-2 text-sm flex items-center justify-center',
        critical && 'bg-red-50 border-red-200 text-red-800',
        high && 'bg-amber-50 border-amber-200 text-amber-800',
        signal.severity === 'info' && 'bg-blue-50 border-blue-200 text-blue-800',
      )}
    >
      <div className="max-w-7xl w-full flex items-center justify-center">
        <div className="flex items-center mr-3">
          {critical ? (
            <AlertCircle className="h-4 w-4 mr-2 text-red-600" />
          ) : high ? (
            <AlertTriangle className="h-4 w-4 mr-2 text-amber-600" />
          ) : (
            <Info className="h-4 w-4 mr-2 text-blue-600" />
          )}
          <span
            className={cn(
              'text-[10px] font-bold uppercase tracking-wider mr-2 px-1.5 py-0.5 rounded',
              critical && 'bg-red-200 text-red-900',
              high && 'bg-amber-200 text-amber-900',
              signal.severity === 'info' && 'bg-blue-200 text-blue-900',
            )}
          >
            {signal.severity}
          </span>
        </div>

        <span className="font-medium truncate max-w-xl">{signal.title}</span>

        {signal.link && (
          <a
            href={signal.link}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'ml-3 inline-flex items-center hover:underline whitespace-nowrap',
              critical && 'text-red-700',
              high && 'text-amber-700',
              signal.severity === 'info' && 'text-blue-700',
            )}
          >
            Full details
            <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        )}

        <button
          onClick={handleDismiss}
          aria-label="Dismiss news banner"
          className={cn(
            'ml-4 p-1 rounded-full hover:bg-opacity-20 hover:bg-gray-500',
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
